// Mint-Pipeline-FromTask / chi tiết phiếu (d21 của Mint-Pipeline): dữ liệu phái sinh từ Deal, theo code board.
import { isOpenDeal, STAGE_LABEL, STAGE_ORDER } from './logic';
import type { Deal, DealStage, ServiceCode } from './types';

export const SVC_ALL: readonly ServiceCode[] = ['FRT', 'TRF', 'CCF', 'DOC', 'DDL', 'SCY', 'PTF', 'FUM', 'ISR', 'OTH'];
export const SVC_NAME: Record<ServiceCode, string> = { FRT: 'Cước', TRF: 'Vận tải', CCF: 'Hải quan', DOC: 'C/O', DDL: 'Giao nhận', SCY: 'Bãi', PTF: 'Phí cảng', FUM: 'Hun trùng', ISR: 'Bảo hiểm', OTH: 'Khác' };
export const FOLLOW_OPTS = ['Hôm nay', '+2 ngày LV · 26/Oct', '+5 ngày LV · 29/Oct', 'Không follow'] as const;

export const DEAL_JOBS: Record<string, string[]> = { GREENBUD: ['SE260639198', 'SE260639186'], THACO: ['SE260639204'], SAVITA: ['SE260639177'], COCOGROUP: ['SE260639160'], JVS: ['SE260639171'] };
export const SVC_QUOTES: readonly [string, string][] = [['TRF · kéo cont Bình Dương → Cát Lái', 'gửi 18/Oct/2026'], ['CCF · khai hải quan xuất', 'gửi 02/Oct/2026']];

export type StepState = 'past' | 'on' | 'todo';
/** Mới → Chờ giá → Đã báo → Won / Lost: bước đã qua + bước hiện tại tô màu (Won / Lost: chỉ bước hiện tại). */
export function stepper(d: Deal): { stage: DealStage; label: string; state: StepState }[] {
  const cur = STAGE_ORDER.indexOf(d.stage);
  return STAGE_ORDER.map((stage, i) => ({ stage, label: STAGE_LABEL[stage], state: i === cur ? 'on' : i < cur && cur < 4 ? 'past' : 'todo' }));
}

export type StageAct = { key: string; label: string; primary: boolean };
export function stageActs(d: Deal): StageAct[] {
  if (!isOpenDeal(d)) return [{ key: 'reopen', label: 'Mở lại phiếu', primary: true }];
  return [
    d.stage === 'New' ? { key: 'pricing', label: 'Chuyển Chờ giá', primary: false } : null,
    d.stage !== 'Quoted' ? { key: 'quoted', label: 'Đánh dấu Đã báo', primary: false } : null,
    { key: 'won', label: 'Chốt Won', primary: true },
    { key: 'lost', label: 'Chốt Lost', primary: false },
  ].filter((x): x is StageAct => !!x);
}

export const pricingLine = (d: Deal): string => (d.pricingRequest ? `${d.pricingRequest} · ${d.pricingStatus}` : 'Chưa hỏi Pricing cho phiếu này');
export const rfqLine = (d: Deal): string => (d.services.includes('TRF') ? 'RFQ-261019-TRF · 2 / 3 đối tác đã phản hồi' : 'Chưa gửi hỏi giá đối tác cho phiếu này');
export const canAsk = (d: Deal): boolean => !d.pricingRequest;
export const canAck = (d: Deal): boolean => d.pricingStatus === 'Đã trả giá';

export interface InqTask { id: string; label: string; due: string; late: boolean; src: string }
export function inquiryTasks(d: Deal): InqTask[] {
  if (d.followDays === null || !isOpenDeal(d)) return [];
  return [{ id: 'f', label: `Follow ${d.id} · ${d.customer}`, due: `Hạn ${d.follow} 10:00`, late: d.followDays < 0, src: 'Tự sinh' }];
}

export interface TodoRow { key: string; label: string; due: string; tone: 'warn' | 'err' | 'pri' | 'mute'; act: string }
/** Cột phải "Việc cần làm": việc follow chưa xong + bước Pricing đang chờ. */
export function todoRows(d: Deal): TodoRow[] {
  const rows: TodoRow[] = inquiryTasks(d).map((k) => ({ key: k.id, label: k.label, due: k.due, tone: k.late ? 'err' : 'warn', act: 'Xong' }));
  if (canAck(d)) rows.push({ key: 'ack', label: 'Xác nhận đã nhận giá Pricing', due: d.pricingRequest, tone: 'pri', act: 'Xác nhận' });
  if (canAsk(d) && isOpenDeal(d)) rows.push({ key: 'ask', label: 'Hỏi Pricing nội bộ', due: 'chưa hỏi', tone: 'mute', act: 'Hỏi' });
  return rows;
}

export const inquiryMails = (d: Deal): { subject: string; from: string; when: string; src: boolean }[] => [
  { subject: `RE: Rate request ${d.subject}`, from: `Liên hệ ${d.customer}`, when: '21/Oct/2026 15:12', src: d.hasMail },
  { subject: 'Booking confirmation tuần 43', from: 'CS · ThuyNTT', when: '20/Oct/2026 09:40', src: false },
  { subject: 'SOA tháng 9', from: `Kế toán ${d.customer}`, when: '18/Oct/2026 11:05', src: false },
];

export function inquiryHistory(d: Deal): { text: string; who: string; when: string; stage?: DealStage }[] {
  const first = d.stage === 'Quoted' && d.quotes[0] ? `Tự chuyển Đã báo khi gửi mail báo giá ${d.quotes[0]}` : `Đổi giai đoạn → ${STAGE_LABEL[d.stage]}`;
  return [
    { text: first, who: 'Hệ thống', when: '21/Oct/2026 15:20', stage: d.stage },
    ...(d.pricingRequest ? [{ text: `Tự chuyển Chờ giá khi hỏi Pricing ${d.pricingRequest}`, who: 'Hệ thống', when: '21/Oct/2026 09:46', stage: 'Pricing' as DealStage }] : []),
    { text: `Đặt follow ${d.follow || '—'}`, who: d.sale, when: '20/Oct/2026 17:02' },
    { text: d.hasMail ? 'Tạo phiếu từ mail khách · giữ liên kết mail gốc' : 'Tạo phiếu', who: d.sale, when: '19/Oct/2026 10:30' },
  ];
}

export const followChoice = (d: Deal): (typeof FOLLOW_OPTS)[number] =>
  d.followDays === null ? 'Không follow' : d.followDays === 0 ? 'Hôm nay' : d.followDays <= 4 ? '+2 ngày LV · 26/Oct' : '+5 ngày LV · 29/Oct';

export const inquiryInfo = (d: Deal): [string, string][] => [
  ['Mã phiếu', d.id], ['Khách', d.customer], ['Giai đoạn', STAGE_LABEL[d.stage]], ['Dịch vụ', d.services.join(' · ')], ['Chủ đề', d.subject],
  ['Follow', d.follow || 'Không follow'], ['Pricing', pricingLine(d)], ['Báo giá gắn', d.quotes.join(', ') || '—'],
];
